// ── 전자메일 PC 사전 다운로드 + 5분 주기 새 메일 확인 ─────────────────────────────
// 2026-10 요청: "로그인과 동시에 전자메일에 등록된 SMTP/POP3(IMAP) 메일을 받아와 사용자 PC의
// RAM이나 하드디스크에 저장하고, 5분 단위로 새 메일을 확인해 상태표시줄에 알람 (전자메일 접속시
// 로딩시간 단축)".
//  - RAM: emailClientCache.js(탭 메모리 Map) / 하드디스크: 브라우저 IndexedDB(아래 persist*).
//  - 로그인 직후 start()가 모든 계정의 폴더·받은편지함 목록(2페이지)·최근 메일 본문(15통)을
//    백그라운드로 내려받아 두 곳에 모두 저장한다(본문은 peek=1 — 서버에서 읽음 처리하지 않음).
//  - 5분마다 GET /email/check로 계정별 안읽음/전체 수 변동만 확인(가벼운 STATUS 호출).
//    늘었으면 해당 계정 캐시를 갱신하고 상태표시줄에 "새 메일 N통" 알람을 띄운다.
//  - 로그아웃(AuthContext.clearSessionLocally)에서 stop()이 RAM+디스크 캐시를 모두 삭제한다.
import API from '../api';
import * as emailCache from './emailClientCache';

let CHECK_INTERVAL_MS = 5 * 60 * 1000; // 기본 5분 — 환경설정 > 메일관리의 새로고침 간격으로 덮어쓴다
const PAGE_SIZE = 30;            // Email.js의 PAGE_SIZE와 동일해야 캐시 키가 일치한다
const PREFETCH_PAGES = 2;
const PREFETCH_BODIES = 15;
const DB_NAME = 'ims_mail_store';
const STORE = 'kv';

let timer = null;
let currentUser = null;
let baseline = new Map();        // accountId -> { unseen, total }
let state = { newCount: 0, perAccount: {} };
const listeners = new Set();

// ── IndexedDB(하드디스크) ──
let dbPromise = null;
function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch (e) { resolve(null); }
  });
  return dbPromise;
}
function userKey(k) { return `${currentUser || ''}::${k}`; }
export async function persistGet(key) {
  const db = await openDb(); if (!db) return undefined;
  return new Promise((resolve) => {
    try {
      const r = db.transaction(STORE).objectStore(STORE).get(userKey(key));
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => resolve(undefined);
    } catch (e) { resolve(undefined); }
  });
}
export async function persistSet(key, data) {
  const db = await openDb(); if (!db) return;
  try { db.transaction(STORE, 'readwrite').objectStore(STORE).put(data, userKey(key)); } catch (e) { /* 무시 */ }
}
// 목록/폴더 캐시만 지운다(메일 본문 ::detail:: 은 내용이 바뀌지 않으므로 유지)
async function persistClearLists(accountId) {
  const db = await openDb(); if (!db) return;
  try {
    const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
    const want = userKey(`${accountId}::`);
    const cur = store.openCursor();
    cur.onsuccess = () => {
      const c = cur.result; if (!c) return;
      const k = String(c.key);
      if (k.startsWith(want) && !k.includes('::detail::')) c.delete();
      c.continue();
    };
  } catch (e) { /* 무시 */ }
}
async function persistClearAll() {
  const db = await openDb(); if (!db) return;
  try { db.transaction(STORE, 'readwrite').objectStore(STORE).clear(); } catch (e) { /* 무시 */ }
}

export function keyDetail(accountId, folder, uid) { return `${accountId}::detail::${folder}::${uid}`; }
export function keyFoldersP(accountId) { return emailCache.keyFolders(accountId); }
export function keyMessagesP(accountId, folder, page) { return emailCache.keyMessages(accountId, folder, page, PAGE_SIZE); }

// 목록·폴더는 서버 응답을 RAM(짧은 TTL)과 디스크(만료 없음, 다음 열기 때 즉시 표시용)에 함께 저장.
export function saveFolders(accountId, list) {
  emailCache.set(keyFoldersP(accountId), list, emailCache.TTL.folders);
  persistSet(keyFoldersP(accountId), list);
}
export function saveMessages(accountId, folder, page, data) {
  emailCache.set(keyMessagesP(accountId, folder, page), data, emailCache.TTL.messages);
  persistSet(keyMessagesP(accountId, folder, page), data);
}
// 계정 변동 시 디스크 캐시도 함께 무효화(RAM은 emailCache.clearAccount)
export function invalidateAccount(accountId) {
  emailCache.clearAccount(accountId);
  persistClearLists(accountId);
}

// ── 상태(알람) ──
function emit() { listeners.forEach(fn => { try { fn({ ...state }); } catch (e) { /* 무시 */ } }); }
export function subscribe(fn) { listeners.add(fn); fn({ ...state }); return () => listeners.delete(fn); }
export function getState() { return { ...state }; }
export function acknowledge() { state = { newCount: 0, perAccount: {} }; emit(); }

// ── 사전 다운로드 ──
async function prefetchAccount(account) {
  const id = account.id;
  try {
    const fr = await API.get('/email/folders', { params: { accountId: id } });
    saveFolders(id, fr.data.folders || []);
  } catch (e) { return; }
  let firstPageMessages = [];
  for (let p = 1; p <= PREFETCH_PAGES; p++) {
    try {
      const r = await API.get('/email/messages', { params: { accountId: id, folder: 'INBOX', page: p, pageSize: PAGE_SIZE } });
      const data = { messages: r.data.messages || [], total: r.data.total || 0 };
      saveMessages(id, 'INBOX', p, data);
      if (p === 1) firstPageMessages = data.messages;
      if (data.messages.length < PAGE_SIZE) break;
    } catch (e) { break; }
  }
  // 최근 메일 본문 — 안읽은 메일도 읽음 처리되지 않도록 peek=1
  for (const m of firstPageMessages.slice(0, PREFETCH_BODIES)) {
    if (!currentUser) return; // 중간에 로그아웃되면 중단
    try {
      const r = await API.get(`/email/messages/${encodeURIComponent(m.uid)}`, { params: { accountId: id, folder: 'INBOX', peek: 1 } });
      persistSet(keyDetail(id, 'INBOX', m.uid), r.data);
    } catch (e) { /* 개별 실패는 건너뜀 */ }
  }
}

async function loadAccounts() {
  const r = await API.get('/email/accounts');
  return r.data.accounts || [];
}

async function check() {
  if (!currentUser) return;
  try {
    const r = await API.get('/email/check');
    const accounts = await loadAccounts().catch(() => []);
    const nameOf = (id) => { const a = accounts.find(x => x.id === id); return a ? (a.name || a.email || a.username || `계정${id}`) : `계정${id}`; };
    for (const a of (r.data.accounts || [])) {
      if (!a.ok) continue;
      const prev = baseline.get(a.accountId);
      baseline.set(a.accountId, { unseen: a.unseen, total: a.total });
      if (!prev) continue;
      // IMAP: 안읽음 증가 또는 전체 수 증가 / POP3: 전체 수 증가
      const inc = a.protocol === 'pop3'
        ? (a.total - prev.total)
        : Math.max((a.total ?? 0) - (prev.total ?? 0), (a.unseen ?? 0) - (prev.unseen ?? 0));
      if (inc > 0) {
        state = {
          newCount: state.newCount + inc,
          perAccount: { ...state.perAccount, [a.accountId]: { name: nameOf(a.accountId), count: (state.perAccount[a.accountId]?.count || 0) + inc } },
        };
        invalidateAccount(a.accountId);
        const acc = accounts.find(x => x.id === a.accountId);
        if (acc) prefetchAccount(acc); // 새 메일을 PC에 바로 받아둔다
        emit();
        try { window.dispatchEvent(new CustomEvent('ims:mail-new', { detail: { accountId: a.accountId, count: inc } })); } catch (e) { /* 무시 */ }
      }
    }
  } catch (e) { /* 네트워크 일시 오류는 다음 주기에 재시도 */ }
}

export async function start(username) {
  if (!username || currentUser === username) return;
  stop(false);
  currentUser = username;
  try {
    const accounts = await loadAccounts();
    if (!accounts.length || currentUser !== username) return;
    // 기준값(현재 안읽음/전체 수) 먼저 세워 두고, 이후 5분 주기에서 증가분만 알람
    try {
      const r = await API.get('/email/check');
      (r.data.accounts || []).forEach(a => { if (a.ok) baseline.set(a.accountId, { unseen: a.unseen, total: a.total }); });
    } catch (e) { /* 무시 */ }
    try {
      const ri = await API.get('/sysmail/refresh-interval');
      const m = Number(ri.data && ri.data.minutes);
      if (Number.isFinite(m) && m >= 1 && m <= 120) CHECK_INTERVAL_MS = m * 60 * 1000;
    } catch (e) { /* 기본 5분 유지 */ }
    timer = setInterval(check, CHECK_INTERVAL_MS);
    for (const a of accounts) { if (currentUser !== username) return; await prefetchAccount(a); }
  } catch (e) { /* 메일 계정이 없거나 권한 없음 — 조용히 종료 */ }
}

export function stop(wipe = true) {
  if (timer) { clearInterval(timer); timer = null; }
  currentUser = null;
  baseline = new Map();
  state = { newCount: 0, perAccount: {} };
  emit();
  if (wipe) { emailCache.clearAll(); persistClearAll(); }
}
